/**
 * Badge rarity presentation helpers — Phase MP-9A + MEM-BADGE-2B
 *
 * Centralises the visual mapping for rarity tiers so Member Dashboard,
 * Badge Center, and the celebration modal stay in lockstep.
 *
 * Backend metadata consumed: badge.badge_rarity, badge.rarity_color,
 * badge.rarity_animated.
 *
 * MEM-BADGE-2B additions:
 *   • LOCKED_RARITY_STYLES — subdued rarity chrome for locked/visible
 *     cards so Members can see the reward tier they're chasing.
 *   • CATEGORY_META — icon + tint per category (secondary hierarchy;
 *     never competes with rarity).
 *   • formatNextStep(view) — turns raw progress into human copy
 *     ("Cast 3 more votes"). Rank / compound / already-earned cases
 *     fall back to safe generics.
 */

export const RARITY_TIERS = ['common', 'rare', 'elite', 'legendary', 'mythic'];

export const RARITY_LABEL = {
  common:    'Common',
  rare:      'Rare',
  elite:     'Elite',
  legendary: 'Legendary',
  mythic:    'Mythic',
};

/** Full presentation for EARNED badges — bold rarity chrome. */
export const RARITY_STYLES = {
  common: {
    chip:    'border-zinc-500/50 text-zinc-300 bg-zinc-500/10',
    ring:    'border-zinc-700',
    glow:    'shadow-none',
    icon:    'text-zinc-300',
    iconBg:  'bg-zinc-800',
    text:    'text-zinc-200',
    accent:  'text-zinc-400',
    bar:     'from-zinc-500 to-zinc-400',
  },
  rare: {
    chip:    'border-sky-500/50 text-sky-300 bg-sky-500/10',
    ring:    'border-sky-500/40',
    glow:    'shadow-[0_0_24px_-8px_rgba(56,189,248,0.5)]',
    icon:    'text-sky-300',
    iconBg:  'bg-sky-500/15',
    text:    'text-sky-100',
    accent:  'text-sky-300',
    bar:     'from-sky-400 to-cyan-300',
  },
  elite: {
    chip:    'border-violet-500/50 text-violet-300 bg-violet-500/10',
    ring:    'border-violet-500/40',
    glow:    'shadow-[0_0_30px_-8px_rgba(139,92,246,0.55)]',
    icon:    'text-violet-300',
    iconBg:  'bg-violet-500/15',
    text:    'text-violet-100',
    accent:  'text-violet-300',
    bar:     'from-violet-400 to-fuchsia-300',
  },
  legendary: {
    chip:    'border-amber-500/60 text-amber-300 bg-amber-500/10',
    ring:    'border-amber-500/50',
    glow:    'shadow-[0_0_36px_-8px_rgba(251,191,36,0.6)]',
    icon:    'text-amber-300',
    iconBg:  'bg-amber-500/15',
    text:    'text-amber-100',
    accent:  'text-amber-300',
    bar:     'from-amber-400 to-yellow-300',
  },
  mythic: {
    chip:    'border-rose-500/60 text-rose-300 bg-rose-500/10',
    ring:    'border-rose-500/60',
    glow:    'shadow-[0_0_42px_-6px_rgba(244,63,94,0.7)]',
    icon:    'text-rose-300',
    iconBg:  'bg-rose-500/15',
    text:    'text-rose-100',
    accent:  'text-rose-300',
    bar:     'from-rose-400 via-fuchsia-400 to-violet-300',
  },
};

/**
 * Subdued presentation for LOCKED (visible) badges. Same hue as the
 * earned form but with muted borders, no glow, and dim icon so the
 * reward tier is legible without out-shining actually-earned trophies.
 */
export const LOCKED_RARITY_STYLES = {
  common: {
    chip:    'border-zinc-700 text-zinc-500 bg-transparent',
    ring:    'border-zinc-800',
    iconBg:  'bg-zinc-900',
    icon:    'text-zinc-600',
    bar:     'from-zinc-600 to-zinc-500',
    accent:  'text-zinc-500',
  },
  rare: {
    chip:    'border-sky-500/25 text-sky-400/70 bg-sky-500/5',
    ring:    'border-sky-500/20',
    iconBg:  'bg-sky-500/5',
    icon:    'text-sky-500/60',
    bar:     'from-sky-500 to-cyan-400',
    accent:  'text-sky-400/80',
  },
  elite: {
    chip:    'border-violet-500/25 text-violet-400/70 bg-violet-500/5',
    ring:    'border-violet-500/20',
    iconBg:  'bg-violet-500/5',
    icon:    'text-violet-500/60',
    bar:     'from-violet-500 to-fuchsia-400',
    accent:  'text-violet-400/80',
  },
  legendary: {
    chip:    'border-amber-500/30 text-amber-400/80 bg-amber-500/5',
    ring:    'border-amber-500/25',
    iconBg:  'bg-amber-500/5',
    icon:    'text-amber-500/70',
    bar:     'from-amber-500 to-yellow-400',
    accent:  'text-amber-400/80',
  },
  mythic: {
    // Should never render visibly (mythic_origin is hidden until earned)
    // but define for defensiveness so nothing crashes.
    chip:    'border-rose-500/25 text-rose-400/70 bg-rose-500/5',
    ring:    'border-rose-500/25',
    iconBg:  'bg-rose-500/5',
    icon:    'text-rose-500/60',
    bar:     'from-rose-500 via-fuchsia-500 to-violet-400',
    accent:  'text-rose-400/80',
  },
};

export const getRarityStyle = (rarity) =>
  RARITY_STYLES[rarity] || RARITY_STYLES.common;

export const getLockedRarityStyle = (rarity) =>
  LOCKED_RARITY_STYLES[rarity] || LOCKED_RARITY_STYLES.common;

/** Ordered: earned first, then highest-progress locked, with hidden-locked last. */
export const sortBadgesForDisplay = (badges) => {
  const order = { earned: 0, locked: 1, hidden: 2 };
  return [...(badges || [])].sort((a, b) => {
    const sa = order[a.status] ?? 9;
    const sb = order[b.status] ?? 9;
    if (sa !== sb) return sa - sb;
    if (a.status === 'locked') {
      return (b.completion_percent || 0) - (a.completion_percent || 0);
    }
    const ra = RARITY_TIERS.indexOf(a.badge_rarity);
    const rb = RARITY_TIERS.indexOf(b.badge_rarity);
    return rb - ra;
  });
};

export const CATEGORY_LABEL = {
  participation: 'Participation',
  loyalty:       'Loyalty',
  engagement:    'Engagement',
  special:       'Special',
  content:       'Content',
  community:     'Community',
  draft:         'Draft',
};

/**
 * Subtle category identity — one icon key + one accent per category.
 * Icon keys resolve through BadgeCard.ICON_MAP so nothing new to import.
 * Accent uses semantic muted tones; rarity remains the loud voice.
 */
export const CATEGORY_META = {
  participation: { icon: 'vote',       accent: 'text-sky-300'     },
  community:     { icon: 'users',      accent: 'text-emerald-300' },
  draft:         { icon: 'star',       accent: 'text-amber-300'   },
  engagement:    { icon: 'flame',      accent: 'text-orange-300'  },
  loyalty:       { icon: 'shield',     accent: 'text-violet-300'  },
  content:       { icon: 'eye',        accent: 'text-cyan-300'    },
  special:       { icon: 'sparkles',   accent: 'text-rose-300'    },
};

/* ------------------------------------------------------------------ */
/*  Next-step copy — MEM-BADGE-2B                                     */
/*                                                                    */
/*  Turns raw progress into an actionable sentence for locked badges. */
/*  NEVER call this for earned badges (returns null defensively).     */
/*  Rank-cap and compound requirements fall back to safe generics.    */
/* ------------------------------------------------------------------ */

const NEXT_STEP_TEMPLATES = {
  poll_votes:          (r) => `Cast ${r} more vote${r === 1 ? '' : 's'}`,
  poll_creates:        (r) => `Create ${r} more poll${r === 1 ? '' : 's'}`,
  poll_comments:       (r) => `Leave ${r} more poll comment${r === 1 ? '' : 's'}`,
  discussions_created: (r) => `Start ${r} more discussion${r === 1 ? '' : 's'}`,
  discussion_replies:  (r) => `Post ${r} more repl${r === 1 ? 'y' : 'ies'}`,
  share_content:       (r) => `Share ${r} more piece${r === 1 ? '' : 's'} of content`,
  draft_votes:         (r) => `Vote on ${r} more prospect${r === 1 ? '' : 's'}`,
  mock_drafts_created: (r) => `Complete ${r} more mock draft${r === 1 ? '' : 's'}`,
  engagement_score:    (r) => `Earn ${r} more engagement point${r === 1 ? '' : 's'}`,
  content_views:       (r) => `Explore ${r} more piece${r === 1 ? '' : 's'} of content`,
  days_member:         (r) => `${r} day${r === 1 ? '' : 's'} remaining`,
};

/**
 * Requirement types where "N more" copy would be nonsensical because
 * the requirement is inherently non-progressive (rank-cap / historical
 * position — you were the 100th member or you weren't).
 */
const NON_PROGRESSIVE_TYPES = new Set([
  'member_number',
  'team_member_number',
]);

/**
 * @param {object} view — a badge view from /api/member/badges
 * @returns {string|null} human next-step copy, or null when N/A
 */
export const formatNextStep = (view) => {
  if (!view || view.status === 'earned') return null;
  const req = view.unlock_requirement || {};
  const type = req.type;
  const progress = Number(view.progress_value || 0);
  const target   = Number(view.target_value   || 0);
  if (target <= 0) return null;
  const remaining = Math.max(0, target - progress);
  if (remaining === 0) return null;

  if (NON_PROGRESSIVE_TYPES.has(type)) {
    // "Founding Member" / "First Hundred Team" — awarded automatically
    // based on your registration position. No player action possible.
    return 'Awarded based on your join order';
  }

  if (type === 'compound') {
    // Only mythic_origin uses this, and it's hidden until earned, so
    // this branch is defensive only.
    return 'Multi-part achievement · keep building';
  }

  const template = NEXT_STEP_TEMPLATES[type];
  if (template) return template(remaining);

  // Unknown future requirement type — safe generic.
  return `${remaining} to go`;
};

/* ------------------------------------------------------------------ */
/*  Celebration presentation — MEM-BADGE-2C                           */
/*                                                                    */
/*  Per-rarity tokens for the earned-badge celebration modal and      */
/*  toast. Sits on TOP of RARITY_STYLES (which owns the base visual   */
/*  hierarchy) — never overrides it. Only adds motion + halo intensity*/
/*  so celebrations feel progressively more significant.              */
/* ------------------------------------------------------------------ */

export const CELEBRATION_STYLES = {
  common: {
    // Modal
    haloClass:    'bg-[radial-gradient(circle_at_center,rgba(161,161,170,0.35),transparent_65%)]',
    ringPulse:    false,
    shineSweep:   false,
    particles:    false,
    sparkleTint:  'text-zinc-300',
    accentText:   'text-zinc-200',
    // Toast (sonner classNames)
    toastClass:   '!bg-zinc-950 !border !border-zinc-700 !text-zinc-100',
    toastTitle:   '!text-zinc-100 !font-heading !uppercase !tracking-widest',
    toastDesc:    '!text-zinc-400',
    toastAction:  '!bg-zinc-800 !text-zinc-100 hover:!bg-zinc-700',
  },
  rare: {
    haloClass:    'bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.5),transparent_65%)]',
    ringPulse:    true,
    shineSweep:   true,
    particles:    false,
    sparkleTint:  'text-sky-300',
    accentText:   'text-sky-200',
    toastClass:   '!bg-zinc-950 !border !border-sky-500/50 !text-sky-50',
    toastTitle:   '!text-sky-200 !font-heading !uppercase !tracking-widest',
    toastDesc:    '!text-sky-100/80',
    toastAction:  '!bg-sky-500/20 !text-sky-200 hover:!bg-sky-500/30 !border !border-sky-500/40',
  },
  elite: {
    haloClass:    'bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.55),transparent_65%)]',
    ringPulse:    true,
    shineSweep:   true,
    particles:    true,
    sparkleTint:  'text-violet-300',
    accentText:   'text-violet-100',
    toastClass:   '!bg-zinc-950 !border !border-violet-500/50 !text-violet-50',
    toastTitle:   '!text-violet-200 !font-heading !uppercase !tracking-widest',
    toastDesc:    '!text-violet-100/80',
    toastAction:  '!bg-violet-500/20 !text-violet-200 hover:!bg-violet-500/30 !border !border-violet-500/40',
  },
  legendary: {
    haloClass:    'bg-[radial-gradient(circle_at_center,rgba(251,191,36,0.6),transparent_60%)]',
    ringPulse:    true,
    shineSweep:   true,
    particles:    true,
    sparkleTint:  'text-amber-300',
    accentText:   'text-amber-100',
    toastClass:   '!bg-zinc-950 !border !border-amber-500/60 !text-amber-50 shadow-[0_0_36px_-8px_rgba(251,191,36,0.6)]',
    toastTitle:   '!text-amber-200 !font-heading !uppercase !tracking-widest',
    toastDesc:    '!text-amber-100/80',
    toastAction:  '!bg-amber-500/20 !text-amber-200 hover:!bg-amber-500/30 !border !border-amber-500/40',
  },
  mythic: {
    haloClass:    'bg-[radial-gradient(circle_at_center,rgba(244,63,94,0.65),rgba(139,92,246,0.35)_35%,transparent_70%)]',
    ringPulse:    true,
    shineSweep:   true,
    particles:    true,
    sparkleTint:  'text-rose-300',
    accentText:   'text-rose-100',
    toastClass:   '!bg-zinc-950 !border !border-rose-500/60 !text-rose-50 shadow-[0_0_40px_-6px_rgba(244,63,94,0.7)]',
    toastTitle:   '!text-rose-200 !font-heading !uppercase !tracking-widest',
    toastDesc:    '!text-rose-100/80',
    toastAction:  '!bg-rose-500/20 !text-rose-200 hover:!bg-rose-500/30 !border !border-rose-500/40',
  },
};

export const getCelebrationStyle = (rarity) =>
  CELEBRATION_STYLES[rarity] || CELEBRATION_STYLES.common;
